import { Bar, BarChart, CartesianGrid, XAxis } from "recharts"

import { ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart"
import { formatMoney } from "@/lib/formatters"
import { ChartFrame, type ChartFrameRow } from "@/features/staff/reports/charts/chart-frame"
import { tickInterval } from "@/features/staff/reports/charts/series"
import { tReports, tSharedReports } from "@/features/staff/reports/format"

/**
 * Columns. Not a line, and the choice is not stylistic.
 *
 * A service day is a discrete event, not a sample of a continuous quantity. A
 * line drawn between Sunday and Monday describes a collapse nobody lived
 * through: a restaurant's week is not noise around a trend, it IS the signal.
 * The same argument is already written down in `features/staff/stats`, which
 * draws its daily series as columns for this reason.
 *
 * **Colour carries nothing here.** One series has no identity to encode, and a
 * sales bar drawn in red reads as an alarm the moment it goes down — the data
 * has no opinion and the fill must not lend it one. Neutral `--foreground` is
 * also the only ink that survives grayscale and every kind of colour blindness,
 * and it sidesteps `--chart-1..5` entirely, which this repo has documented as
 * defective (`--chart-3` reads grey, `--chart-4` clears 1.72:1 against a card,
 * and the set flips hue between themes).
 *
 * The config is a MODULE-LEVEL const on purpose. `ChartStyle` renders a
 * `<style>` block keyed off it, and an object literal declared inside the
 * component is a new object every render, so the block would be re-injected on
 * every single one. `color` rather than the `theme: {light, dark}` pair for the
 * same class of reason: the indirection bottoms out at a token that `.dark`
 * redefines, so a theme toggle repaints with zero React involvement.
 */
const COLUMN_CONFIG = {
  value: { color: "var(--foreground)" },
} satisfies ChartConfig

export type ColumnPoint = {
  key: string
  /** Full label: the tooltip header and the table's row header. */
  label: string
  /** Short label for the axis tick. */
  tick: string
  /**
   * Exact, and `null` ONLY for a bucket with no close in it. A bucket that
   * traded and sold nothing is `"0.0"`, which draws a zero-height column and
   * prints as a zero — the two facts this screen exists to keep apart.
   */
  amount: string | null
  count: number
  /** Bar height, in cents. Geometry; never rendered as text. */
  value: number | null
}

export function ColumnChart({
  label,
  note,
  points,
  bucketColumnLabel,
  testId,
}: {
  label: string
  note?: string
  points: ColumnPoint[]
  bucketColumnLabel: string
  testId: string
}) {
  const rows: ChartFrameRow[] = points.map((point) => ({
    key: point.key,
    cells: [
      point.label,
      point.amount === null ? tSharedReports("charts.no_close") : formatMoney(point.amount),
      point.amount === null ? "—" : tReports("transactions_count", { count: point.count }),
    ],
  }))

  return (
    <ChartFrame
      label={label}
      note={note}
      config={COLUMN_CONFIG}
      testId={testId}
      columns={[
        { key: "bucket", label: bucketColumnLabel },
        { key: "amount", label: tReports("chart_amount_column") },
        { key: "count", label: tReports("chart_transactions_column") },
      ]}
      rows={rows}
    >
      <BarChart
        accessibilityLayer
        data={points}
        margin={{ top: 4, right: 0, bottom: 0, left: 0 }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="tick"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          interval={tickInterval(points.length)}
        />
        {/* The hover cursor is already `fill-muted` through the container's own
            class list, so it is not re-declared here. */}
        <ChartTooltip
          content={
            <ChartTooltipContent
              hideIndicator
              labelFormatter={(_value, payload) =>
                (payload?.[0]?.payload as ColumnPoint | undefined)?.label ?? ""
              }
              // Defect 3 of `components/ui/chart` worked around where it has to
              // be: the primitive cannot know that a number is money, so it
              // formats with `toLocaleString()`. This screen's amounts arrive as
              // exact decimal strings built from integer cents, and re-deriving
              // one through the bar's float height would break the very identity
              // R1 rests on. The formatter reads the ORIGINAL string off the
              // payload and never looks at `value`.
              formatter={(_value, _name, item) => {
                const point = item?.payload as ColumnPoint | undefined
                if (!point) return null

                return (
                  <span className="flex flex-1 items-center justify-between gap-4">
                    <span className="text-muted-foreground">
                      {point.amount === null
                        ? tSharedReports("charts.no_close")
                        : tReports("transactions_count", { count: point.count })}
                    </span>
                    {point.amount === null ? null : (
                      <span className="font-semibold tabular-nums text-foreground">
                        {formatMoney(point.amount)}
                      </span>
                    )}
                  </span>
                )
              }}
            />
          }
        />
        {/* A bucket with no close carries `value: null`, and recharts draws
            nothing for it — the hole in the axis IS the absence. `radius` on the
            top corners only, so a column reads as growing off the baseline. */}
        <Bar dataKey="value" fill="var(--color-value)" radius={[3, 3, 0, 0]} maxBarSize={48} />
      </BarChart>
    </ChartFrame>
  )
}
